// src/components/AppDownloadSection.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Smartphone, Star, ArrowRight, Leaf } from 'lucide-react';

const AppDownloadSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'app-download');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching app config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // If loading, hidden, or inactive → show nothing
  if (loading || !config || config.active === false) return null;

  const playActive = config.playStoreUrl && config.playStoreActive !== false;
  const appActive = config.appStoreUrl && config.appStoreActive !== false;
  if (!playActive && !appActive) return null;

  // ✅ Dynamic Colors
  const bgFrom = config.bgGradientFrom || '#F5AEC0';
  const bgTo = config.bgGradientTo || '#DC829A';
  const titleColor = config.titleColor || '#FFFFFF';
  const descColor = config.descriptionColor || '#FFF2F6';
  const badgeBg = config.badgeBg || 'rgba(255,255,255,0.2)';
  const badgeText = config.badgeText || '#FFFFFF';
  const learnMoreColor = config.learnMoreColor || '#FFF2F6';

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 anim-fadeInUp">
      <div
        className="relative rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: `linear-gradient(135deg, ${bgFrom} 0%, ${bgTo} 100%)` }}
      >
        {/* Decorative Circles */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white opacity-5 -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white opacity-10 translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-center p-6 md:p-12">

          {/* LEFT: Content */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-md mb-4"
              style={{ backgroundColor: badgeBg, color: badgeText }}
            >
              <Smartphone size={14} />
              <span className="text-xs font-bold uppercase tracking-wider">Mobile App</span>
            </div>

            <h2
              className="text-2xl md:text-4xl font-bold mb-3 leading-tight"
              style={{ color: titleColor }}
            >
              {config.heroTitle || 'Shop Organic Anytime, Anywhere'}
            </h2>

            <p
              className="text-sm md:text-base mb-5 leading-relaxed"
              style={{ color: descColor, opacity: 0.95 }}
            >
              {config.heroDescription || 'Download our app for exclusive discounts, faster checkout, aur order tracking.'}
            </p>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-5">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-yellow-300 text-yellow-300" />
                ))}
              </div>
              <span className="text-xs font-semibold" style={{ color: titleColor }}>
                4.9 ★ (12,000+ reviews)
              </span>
            </div>

            {/* Store Buttons */}
            <div className="flex flex-wrap gap-3">
              {playActive && (
                <a
                  href={config.playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black text-white hover:scale-105 transition-all duration-300 btn-2d shadow-2xl border border-white/10"
                >
                  <svg width="28" height="28" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" fill="#fff"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[9px] opacity-80 uppercase tracking-wider">Get it on</p>
                    <p className="text-sm font-bold leading-tight">Google Play</p>
                  </div>
                </a>
              )}

              {appActive && (
                <a
                  href={config.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black text-white hover:scale-105 transition-all duration-300 btn-2d shadow-2xl border border-white/10"
                >
                  <svg width="28" height="28" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" fill="#fff"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[9px] opacity-80 uppercase tracking-wider">Download on the</p>
                    <p className="text-sm font-bold leading-tight">App Store</p>
                  </div>
                </a>
              )}
            </div>

            {/* Learn More */}
            <Link
              to="/app-download"
              className="inline-flex items-center gap-1 mt-5 text-sm font-semibold hover:underline"
              style={{ color: learnMoreColor }}
            >
              Learn more <ArrowRight size={14} />
            </Link>
          </div>

          {/* RIGHT: Mockup */}
          <div className="flex justify-center md:justify-end">
            <div className="relative">
              <div className="absolute inset-0 bg-white blur-3xl opacity-20 rounded-full"></div>

              <div className="relative w-40 md:w-56 bg-gray-900 rounded-[2rem] p-2 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-white rounded-[1.7rem] overflow-hidden">
                  <div className="bg-gray-900 h-5 flex items-center justify-center">
                    <div className="w-12 h-1 bg-gray-800 rounded-full"></div>
                  </div>
                  {config.mockupImage ? (
                    <img src={config.mockupImage} alt="App preview" className="w-full h-72 object-cover" />
                  ) : (
                    <div className="w-full h-72 bg-gradient-to-br from-pink-50 to-pink-100 flex flex-col items-center justify-center p-3">
                      <Leaf size={40} className="text-pink-500 mb-2" />
                      <p className="text-pink-700 font-bold text-xs text-center">NAVIROSE</p>
                      <p className="text-pink-500 text-[10px] mt-1 text-center">Fresh. Fast. Easy.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;